(function () {
  'use strict';

  class RhfFlashSaleProducts {
    static sectionSelector = '[data-rhf-flash-sale-products]';
    static instanceKey = '__rhfFlashSaleProducts';
    static second = 1000;
    static minute = RhfFlashSaleProducts.second * 60;
    static hour = RhfFlashSaleProducts.minute * 60;
    static mobileGalleryDelay = 2600;

    constructor(section) {
      this.section = section;
      this.timer = null;
      this.productData = new Map();
      this.activeProduct = null;
      this.selectedOptions = [];
      this.activeMediaIndex = 0;
      this.quantity = 1;
      this.preloadedMedia = new Set();
      this.thumbDrag = null;
      this.suppressThumbClick = false;
      this.mediaSlideTimer = null;
      this.mobileGalleryTimer = null;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerUp = this.handlePointerUp.bind(this);
      this.cartOpenWatcher = null;
    }

    init() {
      if (!this.section || this.section[RhfFlashSaleProducts.instanceKey]) return;

      this.section[RhfFlashSaleProducts.instanceKey] = this;
      this.cacheProductData();
      this.bindEvents();
      this.tick();

      this.timer = window.setInterval(() => {
        this.tick();
      }, RhfFlashSaleProducts.second);
    }

    destroy() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      if (this.mediaSlideTimer) {
        window.clearTimeout(this.mediaSlideTimer);
        this.mediaSlideTimer = null;
      }

      this.stopMobileGalleryAutoplay();
      this.stopWatchingCartOpen();

      if (this.section) {
        this.unbindEvents();
        this.section[RhfFlashSaleProducts.instanceKey] = null;
      }
    }

    bindEvents() {
      this.section.addEventListener('click', this.boundHandleClick);
      this.section.addEventListener('pointerdown', this.boundHandlePointerDown);
      document.addEventListener('keydown', this.boundHandleKeydown);
      window.addEventListener('resize', this.boundHandleResize);
      window.addEventListener('pointermove', this.boundHandlePointerMove);
      window.addEventListener('pointerup', this.boundHandlePointerUp);
      window.addEventListener('pointercancel', this.boundHandlePointerUp);
    }

    unbindEvents() {
      this.section.removeEventListener('click', this.boundHandleClick);
      this.section.removeEventListener('pointerdown', this.boundHandlePointerDown);
      document.removeEventListener('keydown', this.boundHandleKeydown);
      window.removeEventListener('resize', this.boundHandleResize);
      window.removeEventListener('pointermove', this.boundHandlePointerMove);
      window.removeEventListener('pointerup', this.boundHandlePointerUp);
      window.removeEventListener('pointercancel', this.boundHandlePointerUp);
    }

    cacheProductData() {
      this.section.querySelectorAll('[data-rhf-product-json]').forEach((script) => {
        try {
          const product = JSON.parse(script.textContent);
          this.productData.set(String(product.id), product);
        } catch (error) {
          // Ignore malformed product data so one bad item does not break the section.
        }
      });
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-rhf-modal-open]');
      if (openButton && this.section.contains(openButton)) {
        event.preventDefault();
        this.openModal(openButton.getAttribute('data-rhf-modal-open'));
        return;
      }

      if (event.target.closest('[data-rhf-modal-close]')) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      const thumbButton = event.target.closest('[data-rhf-modal-thumb]');
      if (thumbButton) {
        event.preventDefault();
        if (this.suppressThumbClick) {
          this.suppressThumbClick = false;
          return;
        }
        const targetIndex = Number(thumbButton.getAttribute('data-rhf-modal-thumb'));
        const direction = targetIndex >= this.activeMediaIndex ? 1 : -1;
        this.setActiveMedia(targetIndex, direction);
        this.restartMobileGalleryAutoplay();
        return;
      }

      if (event.target.closest('[data-rhf-modal-media-prev]')) {
        event.preventDefault();
        this.shiftMedia(-1);
        this.restartMobileGalleryAutoplay();
        return;
      }

      if (event.target.closest('[data-rhf-modal-media-next]')) {
        event.preventDefault();
        this.shiftMedia(1);
        this.restartMobileGalleryAutoplay();
        return;
      }

      const optionButton = event.target.closest('[data-rhf-option-value]');
      if (optionButton && !optionButton.disabled) {
        event.preventDefault();
        this.selectOption(
          Number(optionButton.getAttribute('data-rhf-option-index')),
          optionButton.getAttribute('data-rhf-option-value')
        );
        return;
      }

      if (event.target.closest('[data-rhf-modal-qty-minus]')) {
        event.preventDefault();
        this.setQuantity(this.quantity - 1);
        return;
      }

      if (event.target.closest('[data-rhf-modal-qty-plus]')) {
        event.preventDefault();
        this.setQuantity(this.quantity + 1);
        return;
      }

      if (event.target.closest('.rhf-flash-sale-products__modal-atc')) {
        this.watchCartOpen();
      }

      const buyNowButton = event.target.closest('[data-rhf-modal-buy-now]');
      if (buyNowButton) {
        event.preventDefault();
        this.buyNow(buyNowButton);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.getModal() && !this.getModal().hidden) {
        this.closeModal();
      }
    }

    handleResize() {
      const modal = this.getModal();

      if (modal && !modal.hidden) {
        this.setModalOffset();
        this.restartMobileGalleryAutoplay();
      }
    }

    handlePointerDown(event) {
      const thumbs = event.target.closest('[data-rhf-modal-thumbs]');
      if (!thumbs || !this.section.contains(thumbs)) return;

      this.stopMobileGalleryAutoplay();
      this.thumbDrag = {
        node: thumbs,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        scrollLeft: thumbs.scrollLeft,
        scrollTop: thumbs.scrollTop,
        moved: false
      };
    }

    handlePointerMove(event) {
      if (!this.thumbDrag || !this.thumbDrag.node) return;

      const drag = this.thumbDrag;
      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;

      if (!drag.moved && Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 4) return;

      drag.moved = true;
      drag.node.classList.add('is-dragging');
      drag.node.scrollLeft = drag.scrollLeft - deltaX;
      drag.node.scrollTop = drag.scrollTop - deltaY;
      event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.thumbDrag) return;

      const shouldSuppressClick = this.thumbDrag.moved;
      const swipeDirection = this.getMobileThumbSwipeDirection(this.thumbDrag, event);
      const dragNode = this.thumbDrag.node;
      if (this.thumbDrag.node) {
        this.thumbDrag.node.classList.remove('is-dragging');
      }

      this.thumbDrag = null;

      if (swipeDirection) {
        this.shiftMedia(swipeDirection);
      } else if (shouldSuppressClick && this.isMobileThumbCarousel(dragNode)) {
        this.snapMobileThumbsToActive(dragNode);
      }

      this.restartMobileGalleryAutoplay();

      if (shouldSuppressClick) {
        this.suppressThumbClick = true;
        window.setTimeout(() => {
          this.suppressThumbClick = false;
        }, 120);
      }
    }

    getMobileThumbSwipeDirection(drag, event) {
      if (!drag || !drag.moved || !window.matchMedia('(max-width: 767px)').matches) return 0;
      if (!drag.node || drag.node.scrollWidth <= drag.node.clientWidth + 1) return 0;

      const endX = typeof event.clientX === 'number' ? event.clientX : drag.lastX;
      const deltaX = endX - drag.startX;
      const threshold = Math.max(32, drag.node.clientWidth * 0.12);

      if (Math.abs(deltaX) < threshold) return 0;

      return deltaX < 0 ? 1 : -1;
    }

    isMobileThumbCarousel(thumbs) {
      return Boolean(
        thumbs &&
        window.matchMedia('(max-width: 767px)').matches &&
        thumbs.scrollWidth > thumbs.clientWidth + 1
      );
    }

    snapMobileThumbsToActive(thumbs) {
      if (!thumbs) return;

      const activeThumb = thumbs.querySelector('.rhf-flash-sale-products__modal-thumb.is-active');
      if (!activeThumb) return;

      thumbs.scrollTo({
        left: activeThumb.offsetLeft,
        behavior: 'smooth'
      });
    }

    startMobileGalleryAutoplay() {
      this.stopMobileGalleryAutoplay();

      const modal = this.getModal();
      const media = this.activeProduct && this.activeProduct.media ? this.activeProduct.media : [];

      if (!modal || modal.hidden || media.length <= 1 || !window.matchMedia('(max-width: 767px)').matches) return;

      this.mobileGalleryTimer = window.setInterval(() => {
        const activeModal = this.getModal();
        if (!activeModal || activeModal.hidden || !window.matchMedia('(max-width: 767px)').matches) {
          this.stopMobileGalleryAutoplay();
          return;
        }

        this.shiftMedia(1);
      }, RhfFlashSaleProducts.mobileGalleryDelay);
    }

    stopMobileGalleryAutoplay() {
      if (!this.mobileGalleryTimer) return;

      window.clearInterval(this.mobileGalleryTimer);
      this.mobileGalleryTimer = null;
    }

    restartMobileGalleryAutoplay() {
      this.stopMobileGalleryAutoplay();
      this.startMobileGalleryAutoplay();
    }

    getModal() {
      return this.section.querySelector('[data-rhf-modal]');
    }

    openModal(productId) {
      const product = this.productData.get(String(productId));
      const modal = this.getModal();
      if (!product || !modal) return;

      this.activeProduct = product;
      const firstAvailableVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      this.selectedOptions = firstAvailableVariant ? firstAvailableVariant.options.slice() : [];
      this.activeMediaIndex = 0;
      this.quantity = 1;

      this.renderModal();
      this.setModalOffset();
      modal.hidden = false;
      document.body.classList.add('rhf-flash-sale-products-modal-open');
      this.startMobileGalleryAutoplay();

      const closeButton = modal.querySelector('[data-rhf-modal-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    closeModal() {
      const modal = this.getModal();
      if (!modal) return;

      modal.hidden = true;
      modal.style.removeProperty('--rhf-fsp-modal-top-offset');
      document.body.classList.remove('rhf-flash-sale-products-modal-open');
      this.stopMobileGalleryAutoplay();
    }

    setModalOffset() {
      const modal = this.getModal();
      if (!modal) return;

      const offset = this.getFixedHeaderOffset();

      modal.style.setProperty('--rhf-fsp-modal-top-offset', `${Math.round(Math.max(0, offset))}px`);
    }

    getFixedHeaderOffset() {
      const maxHeaderOffset = 88;
      const headerSelectors = [
        '.shopify-section-main-header',
        '.shopify-section-header',
        '#shopify-section-header',
        'sticky-header',
        '.sticky-header',
        '.site-header',
        '.main-header',
        'header'
      ];
      const headers = Array.from(document.querySelectorAll(headerSelectors.join(',')));
      let offset = 0;

      headers.forEach((header) => {
        const headerStyle = window.getComputedStyle(header);
        const headerRect = header.getBoundingClientRect();
        const isFixedHeader = headerStyle.position === 'fixed' ||
          headerStyle.position === 'sticky';
        const isPinnedToTop = headerRect.top <= 1 && headerRect.bottom > 0;

        if (!isFixedHeader || !isPinnedToTop) return;

        offset = Math.max(offset, Math.min(headerRect.bottom, headerRect.height, maxHeaderOffset));
      });

      return Math.min(offset, maxHeaderOffset);
    }

    renderModal() {
      if (!this.activeProduct) return;

      this.renderMedia();
      this.renderProductInfo();
      this.renderOptions();
      this.updateVariantState();
      this.setQuantity(1);
    }

    renderMedia() {
      const modal = this.getModal();
      const thumbs = modal.querySelector('[data-rhf-modal-thumbs]');
      const media = this.activeProduct.media || [];

      thumbs.innerHTML = media
        .map((item, index) => (
          `<button type="button" class="rhf-flash-sale-products__modal-thumb${index === this.activeMediaIndex ? ' is-active' : ''}" data-rhf-modal-thumb="${index}" aria-label="View image ${index + 1}">
            <img src="${item.thumb || item.src}" alt="${this.escapeHtml(item.alt || this.activeProduct.title)}" loading="lazy">
          </button>`
        ))
        .join('');

      this.setActiveMedia(this.activeMediaIndex);
      this.preloadMedia();
    }

    setActiveMedia(index, direction = 0) {
      const modal = this.getModal();
      const media = this.activeProduct && this.activeProduct.media ? this.activeProduct.media : [];
      if (!modal) return;

      if (!media.length) {
        const image = modal.querySelector('[data-rhf-modal-image]');
        const counters = modal.querySelectorAll('[data-rhf-modal-counter]');
        const arrows = modal.querySelectorAll('[data-rhf-modal-media-prev], [data-rhf-modal-media-next]');
        if (image) image.removeAttribute('src');
        counters.forEach((counter) => {
          counter.textContent = '';
        });
        arrows.forEach((arrow) => {
          arrow.hidden = true;
        });
        return;
      }

      const nextIndex = Math.max(0, Math.min(index, media.length - 1));
      const shouldAnimate = direction && nextIndex !== this.activeMediaIndex;
      this.activeMediaIndex = nextIndex;
      const activeMedia = media[this.activeMediaIndex];
      const image = modal.querySelector('[data-rhf-modal-image]');
      const counters = modal.querySelectorAll('[data-rhf-modal-counter]');

      if (image) {
        this.animateMedia(direction, shouldAnimate);
        image.loading = 'eager';
        image.decoding = 'async';
        image.src = activeMedia.src;
        image.alt = activeMedia.alt || this.activeProduct.title;
      }

      counters.forEach((counter) => {
        counter.textContent = `${this.activeMediaIndex + 1} / ${media.length}`;
      });

      modal.querySelectorAll('[data-rhf-modal-thumb]').forEach((button) => {
        const isActive = Number(button.getAttribute('data-rhf-modal-thumb')) === this.activeMediaIndex;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          this.scrollActiveThumb(button, direction);
        }
      });

      modal.querySelectorAll('[data-rhf-modal-media-prev], [data-rhf-modal-media-next]').forEach((arrow) => {
        arrow.hidden = media.length <= 1;
      });
    }

    shiftMedia(direction) {
      const media = this.activeProduct && this.activeProduct.media ? this.activeProduct.media : [];
      if (!media.length) return;

      const nextIndex = (this.activeMediaIndex + direction + media.length) % media.length;
      this.setActiveMedia(nextIndex, direction);
    }

    scrollActiveThumb(button, direction) {
      const thumbs = button && button.closest('[data-rhf-modal-thumbs]');
      if (!thumbs) return;

      const canScrollX = thumbs.scrollWidth > thumbs.clientWidth + 1;
      const canScrollY = thumbs.scrollHeight > thumbs.clientHeight + 1;

      if (this.isMobileThumbCarousel(thumbs) && canScrollX) {
        thumbs.scrollTo({
          left: button.offsetLeft,
          behavior: direction ? 'smooth' : 'auto'
        });
        return;
      }

      if (!direction || (!canScrollX && !canScrollY)) {
        if (typeof button.scrollIntoView === 'function') {
          button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        return;
      }

      const thumbRect = button.getBoundingClientRect();
      const containerRect = thumbs.getBoundingClientRect();

      if (canScrollX) {
        const edgeSpace = Math.max(18, thumbRect.width * 0.7);
        const isNearEdge = direction > 0
          ? thumbRect.right > containerRect.right - edgeSpace
          : thumbRect.left < containerRect.left + edgeSpace;

        if (isNearEdge) {
          thumbs.scrollBy({
            left: direction * (button.offsetWidth + this.getFlexGap(thumbs)),
            behavior: 'smooth'
          });
          return;
        }
      }

      if (canScrollY) {
        const edgeSpace = Math.max(18, thumbRect.height * 0.7);
        const isNearEdge = direction > 0
          ? thumbRect.bottom > containerRect.bottom - edgeSpace
          : thumbRect.top < containerRect.top + edgeSpace;

        if (isNearEdge) {
          thumbs.scrollBy({
            top: direction * (button.offsetHeight + this.getFlexGap(thumbs)),
            behavior: 'smooth'
          });
          return;
        }
      }

      if (typeof button.scrollIntoView === 'function') {
        button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    getFlexGap(element) {
      const styles = window.getComputedStyle(element);
      const rawGap = styles.rowGap || styles.columnGap || styles.gap;
      const gap = parseFloat(rawGap);

      return Number.isNaN(gap) ? 0 : gap;
    }

    animateMedia(direction, shouldAnimate) {
      const modal = this.getModal();
      const mediaFrame = modal && modal.querySelector('.rhf-flash-sale-products__modal-main-media');
      if (!mediaFrame) return;

      mediaFrame.classList.remove('is-slide-next', 'is-slide-prev');
      if (this.mediaSlideTimer) window.clearTimeout(this.mediaSlideTimer);
      if (!shouldAnimate) return;

      mediaFrame.offsetWidth;
      mediaFrame.classList.add(direction > 0 ? 'is-slide-next' : 'is-slide-prev');

      this.mediaSlideTimer = window.setTimeout(() => {
        mediaFrame.classList.remove('is-slide-next', 'is-slide-prev');
        this.mediaSlideTimer = null;
      }, 280);
    }

    preloadMedia() {
      const media = this.activeProduct && this.activeProduct.media ? this.activeProduct.media : [];

      media.forEach((item) => {
        if (!item.src || this.preloadedMedia.has(item.src)) return;

        this.preloadedMedia.add(item.src);
        const image = new Image();
        image.decoding = 'async';
        image.src = item.src;
      });
    }

    renderProductInfo() {
      const modal = this.getModal();
      const discount = modal.querySelector('[data-rhf-modal-discount]');
      const title = modal.querySelector('[data-rhf-modal-title]');

      if (discount) discount.textContent = `-${this.activeProduct.discount_percent}%`;
      if (title) title.textContent = this.activeProduct.title;
    }

    renderOptions() {
      const modal = this.getModal();
      const optionsWrapper = modal.querySelector('[data-rhf-modal-options]');
      const options = this.activeProduct.options || [];

      optionsWrapper.innerHTML = options
        .map((option, optionIndex) => {
          const values = option.values || [];
          return `
            <div class="rhf-flash-sale-products__modal-option-group">
              <div class="rhf-flash-sale-products__modal-option-head">
                <span class="rhf-flash-sale-products__modal-option-title">${this.escapeHtml(option.name)} *</span>
                ${optionIndex === 1 ? '<span class="rhf-flash-sale-products__modal-guide">Guide</span>' : ''}
              </div>
              <div class="rhf-flash-sale-products__modal-option-values">
                ${values.map((value) => `
                  <button
                    type="button"
                    class="rhf-flash-sale-products__modal-option-button"
                    data-rhf-option-index="${optionIndex}"
                    data-rhf-option-value="${this.escapeHtml(value)}"
                  >${this.escapeHtml(value)}</button>
                `).join('')}
              </div>
            </div>
          `;
        })
        .join('');
    }

    selectOption(optionIndex, value) {
      this.selectedOptions[optionIndex] = value;
      this.updateVariantState();
    }

    updateVariantState() {
      const variant = this.getSelectedVariant() || this.getFallbackVariant();
      const modal = this.getModal();
      if (!variant || !modal) return;

      const variantInput = modal.querySelector('[data-rhf-modal-variant-id]');
      const atcButton = modal.querySelector('.rhf-flash-sale-products__modal-atc');
      const buyNowButton = modal.querySelector('[data-rhf-modal-buy-now]');
      const price = modal.querySelector('[data-rhf-modal-price]');
      const comparePrice = modal.querySelector('[data-rhf-modal-compare-price]');
      const form = modal.querySelector('[data-rhf-modal-form]');

      if (variantInput) variantInput.value = variant.id;
      if (atcButton) {
        atcButton.disabled = !variant.available;
        const label = atcButton.querySelector('span');
        if (label) label.textContent = variant.available ? 'Add to bag' : 'Sold out';
      }
      if (buyNowButton) {
        buyNowButton.disabled = !variant.available;
        buyNowButton.classList.remove('is-loading');
        const label = buyNowButton.querySelector('span');
        if (label) label.textContent = variant.available ? 'Buy now' : 'Sold out';
      }

      const formattedPrice = this.formatMoney(variant.price);
      const formattedComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
        ? this.formatMoney(variant.compare_at_price)
        : '';

      if (price) price.textContent = formattedPrice;
      if (comparePrice) comparePrice.textContent = formattedComparePrice;
      if (form) {
        form.setAttribute('data-rhf-mobile-price', formattedPrice);
        form.setAttribute('data-rhf-mobile-compare-price', formattedComparePrice);
      }

      this.updateOptionButtons();
      this.syncMediaToVariant(variant);
    }

    updateOptionButtons() {
      const modal = this.getModal();
      const buttons = modal.querySelectorAll('[data-rhf-option-value]');

      buttons.forEach((button) => {
        const index = Number(button.getAttribute('data-rhf-option-index'));
        const value = button.getAttribute('data-rhf-option-value');
        const candidateOptions = this.selectedOptions.slice();
        candidateOptions[index] = value;
        const matchingVariant = this.activeProduct.variants.find((variant) => (
          variant.options.every((optionValue, optionIndex) => candidateOptions[optionIndex] === optionValue)
        ));

        button.classList.toggle('is-selected', this.selectedOptions[index] === value);
        const isUnavailable = !matchingVariant || !matchingVariant.available;

        button.classList.toggle('is-unavailable', isUnavailable);
        button.disabled = isUnavailable;
      });
    }

    syncMediaToVariant(variant) {
      if (!variant.featured_media_id || !this.activeProduct.media.length) return;

      const mediaIndex = this.activeProduct.media.findIndex((media) => Number(media.id) === Number(variant.featured_media_id));
      if (mediaIndex >= 0) this.setActiveMedia(mediaIndex);
    }

    getSelectedVariant() {
      if (!this.activeProduct) return null;

      return this.activeProduct.variants.find((variant) => (
        variant.options.every((optionValue, index) => this.selectedOptions[index] === optionValue)
      ));
    }

    getFallbackVariant() {
      if (!this.activeProduct) return null;

      return this.activeProduct.variants.find((variant) => variant.available) || this.activeProduct.variants[0];
    }

    setQuantity(quantity) {
      this.quantity = Math.max(1, quantity);
      const modal = this.getModal();
      if (!modal) return;

      const display = modal.querySelector('[data-rhf-modal-quantity]');
      const input = modal.querySelector('[data-rhf-modal-quantity-input]');

      if (display) display.textContent = String(this.quantity);
      if (input) input.value = String(this.quantity);
    }

    buyNow(button) {
      const variant = this.getSelectedVariant() || this.getFallbackVariant();
      if (!variant || !variant.available || !button) return;

      const label = button.querySelector('span');
      const originalText = label ? label.textContent : button.textContent;
      button.disabled = true;
      button.classList.add('is-loading');
      if (label) {
        label.textContent = 'Processing...';
      } else {
        button.textContent = 'Processing...';
      }

      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variant.id,
          quantity: this.quantity
        })
      })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to add product');
          window.location.href = '/checkout';
        })
        .catch(() => {
          button.disabled = false;
          button.classList.remove('is-loading');
          const activeLabel = button.querySelector('span');
          if (activeLabel) {
            activeLabel.textContent = originalText;
          } else {
            button.textContent = originalText;
          }
        });
    }

    watchCartOpen() {
      this.stopWatchingCartOpen();

      this.cartOpenWatcher = window.setInterval(() => {
        if (document.body.classList.contains('side_Drawer_open')) {
          this.closeModal();
          this.stopWatchingCartOpen();
        }
      }, 150);

      window.setTimeout(() => {
        this.stopWatchingCartOpen();
      }, 6000);
    }

    stopWatchingCartOpen() {
      if (!this.cartOpenWatcher) return;

      window.clearInterval(this.cartOpenWatcher);
      this.cartOpenWatcher = null;
    }

    formatMoney(cents) {
      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function' &&
        typeof moneyFormat !== 'undefined'
      ) {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }

      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    getTargetDate() {
      const rawDate = this.section.getAttribute('data-rhf-countdown-date');
      if (!rawDate) return null;

      let target = new Date(rawDate);
      if (Number.isNaN(target.getTime())) {
        target = new Date(rawDate.replace(/-/g, '/'));
      }

      return Number.isNaN(target.getTime()) ? null : target;
    }

    tick() {
      const target = this.getTargetDate();
      const diff = target ? target.getTime() - Date.now() : 0;

      if (diff <= 0) {
        this.renderTime({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const hours = Math.floor(diff / RhfFlashSaleProducts.hour);
      const minutes = Math.floor((diff % RhfFlashSaleProducts.hour) / RhfFlashSaleProducts.minute);
      const seconds = Math.floor((diff % RhfFlashSaleProducts.minute) / RhfFlashSaleProducts.second);

      this.renderTime({ hours, minutes, seconds });
    }

    renderTime(values) {
      Object.keys(values).forEach((key) => {
        const node = this.section.querySelector(`[data-rhf-time="${key}"]`);
        if (!node) return;

        const valueNode = node.querySelector('span') || node;
        valueNode.textContent = RhfFlashSaleProducts.pad(values[key]);
      });
    }

    static pad(value) {
      return String(Math.max(0, value)).padStart(2, '0');
    }

    static initSection(section) {
      if (!section || section[RhfFlashSaleProducts.instanceKey]) return;

      new RhfFlashSaleProducts(section).init();
    }

    static destroySection(section) {
      const instance = section && section[RhfFlashSaleProducts.instanceKey];
      if (instance) instance.destroy();
    }

    static initAll(root) {
      (root || document)
        .querySelectorAll(RhfFlashSaleProducts.sectionSelector)
        .forEach((section) => RhfFlashSaleProducts.initSection(section));
    }

    static destroyAll(root) {
      (root || document)
        .querySelectorAll(RhfFlashSaleProducts.sectionSelector)
        .forEach((section) => RhfFlashSaleProducts.destroySection(section));
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    RhfFlashSaleProducts.initAll(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    RhfFlashSaleProducts.initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    RhfFlashSaleProducts.destroyAll(event.target);
  });
})();
